import { inject, LOCALE_ID } from '@angular/core';
import { DefaultTranspiler, TranslocoTranspiler, TranspileParams } from '@jsverse/transloco';
import { IntlMessageFormat } from 'intl-messageformat';

/**
 * Matches an ICU control clause: `{name, plural,` / `{name, select,` /
 * `{name, selectordinal,` / `{name, number,` / `{name, date,` / `{name, time,`
 * with any whitespace variant. Used as a cheap guard so we only pay the ICU
 * parse cost for strings that actually need it.
 */
const ICU_PATTERN = /\{\s*[^{}\s,]+\s*,\s*(plural|selectordinal|select|number|date|time)\b/;

/**
 * CSP-safe ICU transpiler for Transloco.
 *
 * Why this exists:
 * `@jsverse/transloco-messageformat` (via `@messageformat/core`) compiles ICU
 * strings with `new Function(...)`, which browsers treat like `eval` and which
 * requires `script-src 'unsafe-eval'`. `intl-messageformat` parses ICU into an
 * AST and interprets it at runtime -> no `eval`/`new Function`, so it works
 * under a strict CSP without `'unsafe-eval'`.
 *
 * This solution here is based on the solution from this thread, but extends it: https://github.com/jsverse/transloco/issues/796
 *
 * Order of operations for every string:
 *   1. `DefaultTranspiler.transpile` resolves Transloco's `{{ param }}`
 *      interpolation (double braces) and nested-key references first. This is
 *      what keeps mixed strings like
 *      `{count, plural, one {1 von {{totalCount}} ...} ...}` working: the inner
 *      `{{totalCount}}` becomes a plain value before ICU ever sees it.
 *   2. If the interpolated result still contains ICU syntax, format it with
 *      `intl-messageformat` using the active language.
 */
export class NoEvalTranslocoTranspiler extends DefaultTranspiler implements TranslocoTranspiler {
  /** Fallback when the active lang hasn't been reported yet (e.g. first paint). */
  private readonly fallbackLocale =
    (inject(LOCALE_ID, { optional: true }) ?? 'en').split('-')[0] || 'en';

  /** Active language, kept up to date via the `onLangChanged` lifecycle hook. */
  private activeLang: string | null = null;

  /** Cache compiled formatters. Key = `locale\0message`. */
  private readonly formatterCache = new Map<string, IntlMessageFormat>();

  /**
   * Transloco calls this whenever the active language changes. It's the
   * reliable, public way to know which locale to feed ICU plural rules,
   * instead of relying on the loader-internal `translation['lang']`.
   */
  public onLangChanged(lang: string): void {
    this.activeLang = lang;
  }

  public override transpile(params: TranspileParams): unknown {
    // Step 1: default transpiler resolves `{{ }}` interpolation + nested keys.
    const value = super.transpile(params);

    // Nothing to do if it's not a string, has no params, or has no ICU syntax.
    if (
      typeof value !== 'string' ||
      !params.params ||
      Object.keys(params.params).length === 0 ||
      !ICU_PATTERN.test(value)
    ) {
      return value;
    }

    const locale = this.resolveLocale(params.translation);

    try {
      return this.getFormatter(value, locale).format(params.params as Record<string, unknown>);
    } catch {
      // Malformed ICU (or an unexpected shape): fail soft so one bad key
      // never blanks out or crashes the surrounding view. Returns the
      // already-interpolated string instead of throwing.
      return value;
    }
  }

  private getFormatter(message: string, locale: string): IntlMessageFormat {
    const cacheKey = `${locale}\u0000${message}`;
    let formatter = this.formatterCache.get(cacheKey);
    if (!formatter) {
      formatter = new IntlMessageFormat(message, locale);
      this.formatterCache.set(cacheKey, formatter);
    }
    return formatter;
  }

  /**
   * Locale priority:
   *   1. Active lang reported by `onLangChanged` (most reliable).
   *   2. `translation['lang']` if the loader set it (default HTTP loader does).
   *   3. `LOCALE_ID` fallback.
   * We strip any region ('de-DE' -> 'de'); CLDR plural rules are language-level
   * and this avoids "unknown locale" edge cases in older environments.
   */
  private resolveLocale(translation: Record<string, unknown> | undefined): string {
    const fromHook = this.activeLang;
    const fromTranslation = typeof translation?.['lang'] === 'string' ? translation['lang'] : null;

    const lang = fromHook ?? fromTranslation ?? this.fallbackLocale;
    return lang.split('-')[0] || this.fallbackLocale;
  }
}
